// React & Hooks
import { useState, useEffect, useContext } from "react";
import moment from "moment";
// MUI Components
import {
  Grid,
  Box,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  TableContainer,
} from "@mui/material";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
// Styles
import "../../../Style/pagestyle.scss";
import { AuthContext } from "../../../Context/AuthContext";
import LoadingPage from "../../../Utils/LoadingPage";
import EmptyData from "../../Include/EmptyData/EmptyData";
import {
  SelectClassByStudentId,
  SelectSubject,
} from "../../Include/DynamicSelect/DynamicSelect";
import { SelectDatePicker } from "../../Include/SelectDatePicker/SelectDatePicker";
import useGetTeachingTimeByStudent from "../../../Hooks/useGetTeachingTimeByStudent";
// import TeachingTimeAction from "./TeachingTimeAction"
import { ButtonCreate } from "../../../Utils/Component";
import PrintTeachingTime from "./PrintTeachingTime";
import useGetStudentById from "../../../Hooks/useGetStudentById";

export default function TeachingTimeForStudent({ studentId }) {
  const { language, t } = useContext(AuthContext);
  // Get classId from query parameters

  const [openPrint, setOpenPrint] = useState(false);
  const handleOpenPrint = () => {
    setOpenPrint(true);
  };
  const handleClosePrint = () => {
    setOpenPrint(false);
  };

  // Filters state
  const [toDate, setToDate] = useState();
  const [classVal, setClassVal] = useState({ id: "", title: "" });
  const [subject, setSubject] = useState({ id: "", title: "" });
  const [fromDate, setFromDate] = useState();

  const { studentData } = useGetStudentById({ id: studentId });

  // Table and pagination data
  const { loading, tableData, handleSearch } = useGetTeachingTimeByStudent({
    fromDate: toDate ? moment(fromDate).format("YYYY-MM-DD") : null,
    toDate: toDate ? moment(toDate).format("YYYY-MM-DD") : null,
    classId: classVal?.id || null,
    subjectId: subject?.id || null,
    studentId: studentId,
  });

  // console.log("tableData::", tableData, studentData);

  // Responsive width
  const [width, setWidth] = useState(window.innerWidth);
  const updateDimensions = () => setWidth(window.innerWidth - 50);

  useEffect(() => {
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  return (
    <div className="page-container ">
      {/* ================== Filters ================== */}
      <Box>
        <Grid container spacing={3}>
          {/* Search Input */}
          {/* <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("search")}
            </Typography>
            <SearchTextField
              className="text-field"
              handleSearch={handleSearch}
            />
          </Grid> */}

          {/* Class Filter */}
          <Grid item xs={6} sm={4} md={3} lg={3} xl={2.4}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_class")}
            </Typography>
            <SelectClassByStudentId
              value={classVal}
              studentId={studentId}
              onChange={setClassVal}
              className="text-field"
            />
          </Grid>

          {/* Subject Filter */}
          <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_subject")}
            </Typography>
            <SelectSubject
              buttonAdd={false}
              value={subject}
              onChange={setSubject}
              className="text-field"
            />
          </Grid>

          {/* From Date */}
          <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_start_date")}
            </Typography>
            <SelectDatePicker value={fromDate} onChange={setFromDate} />
          </Grid>

          {/* To Date */}
          <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_end_date")}
            </Typography>
            <SelectDatePicker value={toDate} onChange={setToDate} />
          </Grid>

          <Grid item xs={12} sm={8} md={12} lg={12} xl={3.6}>
            <ButtonCreate
              handleOpen={handleOpenPrint}
              classBtn={"btn-create"}
              classIcon={"icon-add"}
              title={t("thead_print")}
              icon={
                <PrintRoundedIcon sx={{ color: "#fff" }} fontSize="medium" />
              }
            />
            {openPrint && (
              <PrintTeachingTime
                open={openPrint}
                handleClose={handleClosePrint}
                editData={tableData}
                studentData={studentData}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      {/* ================== Table ================== */}
      <Box className="body-container">
        <TableContainer sx={{ maxWidth: `${width}px` }}>
          <Table className="table" aria-label="teaching-time-table">
            <TableHead className="header-row">
              <TableRow>
                <TableCell
                  className={t("class-name-header-title-start")}
                  width="5%"
                >
                  {t("no")}
                </TableCell>

                {/* <TableCell className={t("class-name-header-title")} width="15%">
                  {t("thead_date")}
                </TableCell> */}

                {/* <TableCell className={t("class-name-header-title")} width="20%">
                  {t("thead_class")}
                </TableCell> */}

                <TableCell className={t("class-name-header-title")} width="10%">
                  {t("thead_start_date")}
                </TableCell>

                <TableCell className={t("class-name-header-title")} width="10%">
                  {t("thead_end_date")}
                </TableCell>

                <TableCell className={t("class-name-header-title")} width="15%">
                  {t("thead_subject")}
                </TableCell>

                <TableCell className={t("class-name-header-title")} width="35%">
                  {t("thead_lesson")}
                </TableCell>

                {/* <TableCell
                  className={t("class-name-header-title")}
                  width="20%"
                  align="center"
                >
                  {t("thead_time")}
                </TableCell> */}

                <TableCell className={t("class-name-header-title")} width="20%">
                  {t("thead_remark")}
                </TableCell>
                <TableCell
                  className={t("class-name-header-title-end")}
                  width="5%"
                />
              </TableRow>
            </TableHead>

            <TableBody className="body">
              {loading ? (
                <TableRow className="body-row">
                  <TableCell
                    colSpan={8}
                    className="body-cell"
                    sx={{ bgcolor: "#fff" }}
                  >
                    <LoadingPage />
                  </TableCell>
                </TableRow>
              ) : tableData?.length === 0 || tableData?.length === undefined ? (
                <TableRow className="body-row">
                  <TableCell colSpan={8} className="body-cell">
                    <EmptyData />
                  </TableCell>
                </TableRow>
              ) : (
                tableData?.map((row, index) => (
                  <TableRow className="body-row" key={row._id || index}>
                    <TableCell className="body-cell-start">
                      {index + 1}
                    </TableCell>

                    <TableCell className="body-cell">
                      {row?.fromDate &&
                        moment(row?.fromDate).format("DD MMM YYYY")}
                    </TableCell>

                    <TableCell className="body-cell">
                      {row?.toDate && moment(row?.toDate).format("DD MMM YYYY")}
                    </TableCell>

                    <TableCell className="body-cell">
                      {language === "en"
                        ? row?.subjectId?.enName
                        : row?.subjectId?.khName}
                    </TableCell>

                    <TableCell
                      className="body-cell"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {row?.lesson}
                    </TableCell>

                    <TableCell className="body-cell">{row?.remark}</TableCell>
                    <TableCell className="body-cell-end" align="right">
                      {/* <TeachingTimeAction
                        editData={row}
                        classId={classId}
                        setRefetch={refetch}
                      /> */}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </div>
  );
}
